import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import Modal from '../components/Modal';
import { ImageDetailContent } from '../components/ImageDetailPage';
import { useContent } from './ContentContext';
import { ImagePreviewIndex } from '../utils/imagePreview';
import { useImageFlick } from '../hooks/useImageFlick';

type OpenPreview = (event: React.MouseEvent<HTMLAnchorElement>, href: string, index?: ImagePreviewIndex) => void;
const ImagePreviewContext = createContext<OpenPreview>(() => {});
export const useImagePreview = () => useContext(ImagePreviewContext);

export function ImagePreviewProvider({ children, routeKey }: { children: React.ReactNode; routeKey: string }) {
    const { t } = useContent();
    const [selection, setSelection] = useState<{ id: string; hash: string; year: string; month: string; search: string; route: string; index?: ImagePreviewIndex } | null>(null);
    const [navigationHost, setNavigationHost] = useState<HTMLDivElement | null>(null);
    const navigate = useCallback((href: string, index?: ImagePreviewIndex) => {
        const params = new URL(href, window.location.origin).searchParams;
        setSelection(current => current ? { ...current, id: params.get('id')!, hash: params.get('hash')!, year: params.get('year')!, month: params.get('month')!, search: params.toString(), index } : null);
    }, []);
    const close = useCallback(() => setSelection(null), []);
    useEffect(() => { close(); }, [routeKey, close]);
    useEffect(() => {
        window.addEventListener('popstate', close);
        window.addEventListener('hashchange', close);
        return () => { window.removeEventListener('popstate', close); window.removeEventListener('hashchange', close); };
    }, [close]);
    const open = useCallback<OpenPreview>((event, href, index) => {
        if (event.defaultPrevented || event.button !== 0 || event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return;
        const params = new URL(href, window.location.origin).searchParams;
        const id = params.get('id'), hash = params.get('hash'), year = params.get('year'), month = params.get('month');
        if (!id || !/^\d+$/.test(id) || !hash || !year || !month) return;
        event.preventDefault();
        const matching = index?.image.hash === hash && String(index.image.id) === id ? index : undefined;
        setSelection({ id, hash, year, month, search: params.toString(), route: routeKey, index: matching });
    }, [routeKey]);
    const visible = selection?.route === routeKey ? selection : null;
    useImageFlick(!!visible, close);
    return <ImagePreviewContext.Provider value={open}>
        {children}
        <Modal helpSection="image-detail" isOpen={!!visible} onClose={close} title={visible ? t('detail.illustrationTitle', { id: visible.id }) : t('imageDetail.title')} panelClassName="lab-image-preview"
            headerContent={<div className="lab-image-header-navigation" ref={setNavigationHost} />}
            restoreFocusSelector="main h1">
            {visible && <div className="p-4 sm:p-6"><ImageDetailContent imageId={visible.id}
                imageHash={visible.hash} year={visible.year} month={visible.month} contextSearch={visible.search} mode="modal" navigationHost={navigationHost} onNavigate={navigate} previewIndex={visible.index} /></div>}
        </Modal>
    </ImagePreviewContext.Provider>;
}
